import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  buildModelParamsPayload,
  createDefaultModelCalculationParamSelection,
  isSameModelParamSelection,
} from '../utils/modelCalculationParams'
import { createModel, getModelParamDefinitions } from '../services/modelService'
import type { ModelAddFormValues, ModelCalculationParamSelection, ModelParamDefinition } from '../types/modelParam'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import { getModelEditReactPath, getModelListReactPath } from '@/lib/utils/modelNavigation'

interface BasicInfoSnapshot {
  model_name: string
  description: string
  is_active: 0 | 1
  params: ModelCalculationParamSelection
}

function to01(value: unknown): 0 | 1 {
  if (value === true) return 1
  if (value === false) return 0
  const n = Number(value)
  return n ? 1 : 0
}

const EMPTY_FORM: ModelAddFormValues = {
  model_name: '',
  description: '',
  is_active: 0,
  calculationParams: {},
}

export function useModelAdd() {
  const navigate = useNavigate()
  const [definitions, setDefinitions] = useState<ModelParamDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [validateForm, setValidateForm] = useState(false)
  const [form, setForm] = useState<ModelAddFormValues>(EMPTY_FORM)
  const [processorFile, setProcessorFile] = useState<File | null>(null)
  const [pklFile, setPklFile] = useState<File | null>(null)
  const originalRef = useRef<BasicInfoSnapshot | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      try {
        const defs = await getModelParamDefinitions()
        if (cancelled) return
        const defaultParams = createDefaultModelCalculationParamSelection(defs)
        const initialForm: ModelAddFormValues = {
          model_name: '',
          description: '',
          is_active: 0,
          calculationParams: defaultParams,
        }
        setDefinitions(defs)
        setForm(initialForm)
        originalRef.current = {
          model_name: '',
          description: '',
          is_active: 0,
          params: { ...defaultParams },
        }
      } catch (error) {
        if (!cancelled) {
          showApiError(error)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  const getSnapshot = useCallback(
    (current: ModelAddFormValues): BasicInfoSnapshot => ({
      model_name: current.model_name,
      description: current.description,
      is_active: to01(current.is_active),
      params: current.calculationParams,
    }),
    [],
  )

  const isDirty = useMemo(() => {
    const original = originalRef.current
    if (!original) return false
    const current = getSnapshot(form)
    return (
      current.model_name !== original.model_name ||
      current.description !== original.description ||
      current.is_active !== original.is_active ||
      !isSameModelParamSelection(current.params, original.params, definitions) ||
      !!processorFile ||
      !!pklFile
    )
  }, [definitions, form, getSnapshot, pklFile, processorFile])

  const updateField = <K extends keyof Omit<ModelAddFormValues, 'calculationParams'>>(
    key: K,
    value: ModelAddFormValues[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const updateCalculationParams = (calculationParams: ModelCalculationParamSelection) => {
    setForm((prev) => ({ ...prev, calculationParams }))
  }

  const cancel = () => {
    navigate(getModelListReactPath())
  }

  const submit = async () => {
    if (!isDirty || submitting) return

    setValidateForm(true)

    const trimmedName = form.model_name.trim()
    if (!trimmedName) {
      showError('Model Name is required')
      return
    }

    if (!processorFile) {
      showError('Model Processor File is required')
      return
    }

    setSubmitting(true)
    try {
      const modelId = await createModel({
        model_name: trimmedName,
        description: form.description,
        is_active: to01(form.is_active),
        params: JSON.stringify(buildModelParamsPayload(form.calculationParams, definitions)),
        model_processor_file: processorFile,
        model_pkl_file: pklFile ?? undefined,
      })

      showSuccess('Model created successfully!')
      navigate(getModelEditReactPath(modelId))
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  return {
    definitions,
    loading,
    submitting,
    validateForm,
    form,
    processorFile,
    pklFile,
    isDirty,
    updateField,
    updateCalculationParams,
    setProcessorFile,
    setPklFile,
    cancel,
    submit,
  }
}
